/**
 * RTWO Event Bus & Message Broker — Engine Implementation
 * Phase 12.5 | Module ID: RTWO-EBUS-001
 * Version: 1.0.0-phase12.5
 *
 * Coordinates all Event Bus services through dependency injection,
 * in-memory repositories, engine source adapter registry, and internal event bus.
 *
 * Truthful Data Policy: repositories remain empty until real RTWO engines
 * publish events via the engine source adapter. No fabricated events,
 * subscribers, publishers, delivery confirmations, retry counts, or queue metrics.
 */

import { EVENT_BUS_VERSION } from './domain-models';
import type { RTWOEventBusStatus } from './domain-models';
import type {
  IEventRegistryService,
  ITopicRegistryService,
  IPublisherRegistryService,
  ISubscriberRegistryService,
  IRoutingEngineService,
  IDeliveryEngineService,
  IRetryManagerService,
  IDeadLetterQueueService,
  IMetadataService,
  IEventBusRegistryService,
  IEventPublisherService,
  IEBusReadinessService,
} from './service-interfaces';
import { createEBusEngineServices } from './services';

export class RTWOEventBusEngine {
  private static _instance: RTWOEventBusEngine | null = null;

  readonly moduleId = EVENT_BUS_VERSION.moduleId;
  readonly moduleName = EVENT_BUS_VERSION.moduleName;
  readonly version = EVENT_BUS_VERSION.version;
  readonly phase = EVENT_BUS_VERSION.phase;
  readonly status: RTWOEventBusStatus = 'structure_ready';

  readonly eventRegistry: IEventRegistryService;
  readonly topicRegistry: ITopicRegistryService;
  readonly publisherRegistry: IPublisherRegistryService;
  readonly subscriberRegistry: ISubscriberRegistryService;
  readonly routingEngine: IRoutingEngineService;
  readonly deliveryEngine: IDeliveryEngineService;
  readonly retryManager: IRetryManagerService;
  readonly deadLetterQueue: IDeadLetterQueueService;
  readonly metadataService: IMetadataService;
  readonly busRegistry: IEventBusRegistryService;
  readonly eventPublisher: IEventPublisherService;
  readonly readinessService: IEBusReadinessService;

  private constructor() {
    const { services } = createEBusEngineServices();

    this.eventRegistry = services.eventRegistry;
    this.topicRegistry = services.topicRegistry;
    this.publisherRegistry = services.publisherRegistry;
    this.subscriberRegistry = services.subscriberRegistry;
    this.routingEngine = services.routingEngine;
    this.deliveryEngine = services.deliveryEngine;
    this.retryManager = services.retryManager;
    this.deadLetterQueue = services.deadLetterQueue;
    this.metadataService = services.metadataService;
    this.busRegistry = services.busRegistry;
    this.eventPublisher = services.eventPublisher;
    this.readinessService = services.readinessService;
  }

  static getInstance(): RTWOEventBusEngine {
    if (!RTWOEventBusEngine._instance) {
      RTWOEventBusEngine._instance = new RTWOEventBusEngine();
    }
    return RTWOEventBusEngine._instance;
  }

  async initialize(): Promise<void> {
    await Promise.all([
      this.eventRegistry.initialize(),
      this.topicRegistry.initialize(),
      this.publisherRegistry.initialize(),
      this.subscriberRegistry.initialize(),
      this.routingEngine.initialize(),
      this.deliveryEngine.initialize(),
      this.retryManager.initialize(),
      this.deadLetterQueue.initialize(),
      this.metadataService.initialize(),
      this.busRegistry.initialize(),
      this.eventPublisher.initialize(),
      this.readinessService.initialize(),
    ]);
  }

  getEngineStatus() {
    return {
      moduleId: this.moduleId,
      moduleName: this.moduleName,
      version: this.version,
      phase: this.phase,
      status: this.status,
      productionReadiness: EVENT_BUS_VERSION.productionReadiness,
      policy: EVENT_BUS_VERSION.policy,
      topicCount: this.topicRegistry.getTopicCount(),
      publisherCount: this.publisherRegistry.getActivePublisherCount(),
      subscriberCount: this.subscriberRegistry.getActiveSubscriberCount(),
      eventCount: this.eventRegistry.getEventCount(),
      deliveryCount: this.deliveryEngine.getPendingDeliveryCount(),
      deadLetterCount: this.deadLetterQueue.getDeadLetterCount(),
      retryCount: this.retryManager.getTotalRetryCount(),
    };
  }
}

export const getEventBusEngine = (): RTWOEventBusEngine => RTWOEventBusEngine.getInstance();
